import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  CheckCircle2, 
  Loader2,
  X,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

/**
 * Composant d'alertes automatiques pour les parcelles
 * Affiche les dangers (réseaux, obstacles) avant une intervention
 */
export const AlertesParcelle = ({ 
  parcelle,
  parcelleId,
  typeIntervention,
  materielId,
  onClose,
  onContinue,
  isOpen = false
}) => {
  const [loading, setLoading] = useState(true);
  const [alertes, setAlertes] = useState([]);
  const [nbDangers, setNbDangers] = useState(0);

  useEffect(() => {
    if (isOpen && (parcelle?.id || parcelleId)) {
      verifierAlertes();
    }
  }, [isOpen, parcelle, parcelleId, typeIntervention]);

  const verifierAlertes = async () => {
    setLoading(true);
    try {
      const response = await api.post('/alertes-parcelle/verifier', {
        parcelle_id: parcelle?.id || parcelleId,
        type_intervention: typeIntervention || '',
        materiel_id: materielId || null
      });
      
      setAlertes(response.data.alertes || []);
      setNbDangers(response.data.nb_dangers || 0);
    } catch (error) {
      console.error('Erreur vérification alertes:', error);
      setAlertes([]);
    } finally {
      setLoading(false);
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'danger':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'attention':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getPrioriteColor = (priorite) => {
    switch (priorite) {
      case 'haute':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'moyenne':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      default:
        return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {nbDangers > 0 ? (
              <AlertTriangle className="w-5 h-5 text-red-500" />
            ) : alertes.length > 0 ? (
              <AlertCircle className="w-5 h-5 text-orange-500" />
            ) : (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            )}
            <span>
              {loading ? 'Vérification...' : 
               nbDangers > 0 ? `${nbDangers} danger${nbDangers > 1 ? 's' : ''} détecté${nbDangers > 1 ? 's' : ''}` :
               alertes.length > 0 ? `${alertes.length} point${alertes.length > 1 ? 's' : ''} d'attention` :
               'Aucun danger détecté'}
            </span>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : alertes.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-medium text-green-700">Parcelle sécurisée</p>
              <p className="text-sm text-muted-foreground mt-1">
                Aucun obstacle ou danger détecté sur cette parcelle
              </p>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              {/* Bandeau récapitulatif si dangers */}
              {nbDangers > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-700">Attention requise</p>
                    <p className="text-sm text-red-600">
                      Vérifiez les points suivants avant d'intervenir sur cette parcelle
                    </p>
                  </div>
                </div>
              )}

              {/* Liste des alertes */}
              {alertes.map((alerte, index) => (
                <Card 
                  key={alerte.id || index}
                  className={cn(
                    "border-l-4",
                    alerte.type_alerte === 'danger' && "border-l-red-500",
                    alerte.type_alerte === 'attention' && "border-l-orange-500",
                    alerte.type_alerte === 'info' && "border-l-blue-500"
                  )}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl flex-shrink-0">
                        {alerte.icone || '⚠️'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">
                            {alerte.titre?.replace(/^[^\s]+\s/, '') || 'Alerte'}
                          </span>
                          <Badge 
                            variant="outline" 
                            className={cn("text-xs", getPrioriteColor(alerte.priorite))}
                          >
                            {alerte.priorite === 'haute' ? 'Urgent' : 
                             alerte.priorite === 'moyenne' ? 'Important' : 'Info'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {alerte.description}
                        </p>
                        {alerte.details && (
                          <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                            <Badge variant="secondary" className="text-xs">
                              {alerte.categorie}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Actions */}
        <div className="flex gap-2 pt-4 border-t mt-2">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Annuler
          </Button>
          <Button 
            onClick={onContinue}
            className={cn(
              "flex-1",
              nbDangers > 0 ? "bg-orange-600 hover:bg-orange-700" : "bg-green-600 hover:bg-green-700"
            )}
          >
            {nbDangers > 0 ? (
              <>
                Continuer malgré tout
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Continuer
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Badge d'alerte compact pour afficher sur les cartes/listes
 */
export const AlerteBadge = ({ nbAlertes, nbDangers, onClick }) => {
  if (!nbAlertes && !nbDangers) return null;
  
  return (
    <Badge 
      variant="outline"
      className={cn(
        "cursor-pointer",
        nbDangers > 0 
          ? "bg-red-100 text-red-700 border-red-300 hover:bg-red-200" 
          : "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
      )}
      onClick={onClick}
    >
      {nbDangers > 0 ? (
        <AlertTriangle className="w-3 h-3 mr-1" />
      ) : (
        <AlertCircle className="w-3 h-3 mr-1" />
      )}
      {nbDangers || nbAlertes}
    </Badge>
  );
};

export default AlertesParcelle;
